import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { tv } from "tailwind-variants";
import Icon from "../../icons";
import colors from "../../theme/tokens/colors";
import cn from "../../utils/cn";
const buttonVariants = tv({
    base: `
    flex items-center justify-center
    whitespace-nowrap rounded px-5 text-sm font-bold text-white
    outline-none focus-within:ring-0
    focus-within:ring-offset-0 hover:duration-500 hover:ease-in-out
    focus:outline-none focus:ring-0
    active:scale-95 active:ring-0 active:transition-all
    disabled:cursor-not-allowed disabled:active:scale-100`,
    variants: {
        color: {
            primary: "border-primary bg-primary hover:bg-primary-dark disabled:bg-gray-4 data-[state=open]:bg-gray-4 disabled:text-gray-8",
            secondary: "border-gray bg-gray-9 hover:bg-gray-10 disabled:bg-gray-4 data-[state=open]:bg-gray-4 disabled:text-gray-8",
            success: "border-success bg-success hover:bg-success-10 disabled:bg-gray-4 data-[state=open]:bg-gray-4 disabled:text-gray-8",
            danger: "border-danger bg-danger hover:bg-danger-10 disabled:bg-gray-4 data-[state=open]:bg-gray-4 disabled:text-gray-8",
        },
        variant: {
            contained: "",
            outlined: "border",
            ghost: "bg-transparent text-sm font-normal",
            text: "bg-transparent",
            link: "bg-transparent p-0",
        },
        icon: {
            true: "p-3",
            false: "",
        },
        full: {
            true: "w-full",
            false: "",
        },
        loading: {
            true: "cursor-auto items-center justify-center hover:pointer-events-none",
            false: "",
        },
        size: {
            regular: "h-10 px-5 leading-100",
            condensed: "h-7 px-3 leading-100",
            auto: "h-auto p-0 leading-100",
        },
    },
    compoundVariants: [
        {
            variant: "outlined",
            className: "border-gray-4 bg-white text-gray-10 hover:border-gray-10 hover:bg-gray-2 hover:text-gray-10 disabled:border-gray-light disabled:bg-gray-4 disabled:text-gray-8",
        },
        {
            variant: "text",
            color: "primary",
            className: "bg-transparent text-primary hover:bg-primary-2 hover:text-primary-11 disabled:bg-transparent disabled:text-primary-8",
        },
        {
            variant: "text",
            color: "secondary",
            className: "bg-transparent text-gray-dark hover:bg-gray-2 hover:text-gray-12 disabled:bg-transparent disabled:text-gray-8",
        },
        {
            variant: "text",
            color: "success",
            className: "bg-transparent text-success hover:bg-success-2 hover:text-success-11  disabled:bg-transparent disabled:text-success-8",
        },
        {
            variant: "text",
            color: "danger",
            className: "bg-transparent text-danger hover:bg-danger-2 hover:text-danger-11 disabled:bg-transparent disabled:text-danger-8",
        },
        {
            variant: "link",
            color: "primary",
            className: "bg-transparent p-0 text-primary hover:bg-transparent hover:text-primary-11 disabled:bg-transparent disabled:text-primary-8",
        },
        {
            variant: "link",
            color: "secondary",
            className: "bg-transparent p-0 text-gray-dark hover:bg-transparent hover:text-gray-12 disabled:bg-transparent disabled:text-gray-8",
        },
        {
            variant: "link",
            color: "success",
            className: "bg-transparent p-0 text-success hover:bg-transparent hover:text-success-11 disabled:bg-transparent disabled:text-success-7",
        },
        {
            variant: "link",
            color: "danger",
            className: "bg-transparent p-0 text-danger hover:bg-transparent hover:text-danger-11 disabled:bg-transparent disabled:text-danger-8",
        },
        {
            variant: "ghost",
            color: "primary",
            className: "rounded-none bg-transparent p-0 text-primary hover:bg-transparent hover:text-primary-11 disabled:bg-transparent disabled:text-primary-8",
        },
        {
            variant: "ghost",
            color: "secondary",
            className: "rounded-none bg-transparent p-0 text-gray-dark hover:bg-transparent hover:text-gray-12 disabled:bg-transparent disabled:text-gray-8",
        },
        {
            variant: "ghost",
            color: "success",
            className: "rounded-none bg-transparent p-0 text-success hover:bg-transparent hover:text-success-11 disabled:bg-transparent disabled:text-success-8",
        },
        {
            variant: "ghost",
            color: "danger",
            className: "rounded-none bg-transparent p-0 text-danger hover:bg-transparent hover:text-danger-11 disabled:bg-transparent disabled:text-danger-8",
        },
        {
            icon: true,
            size: "regular",
            className: "p-3 disabled:bg-gray-3",
        },
        {
            icon: true,
            variant: "contained",
            size: "condensed",
            className: "p-1.5 disabled:bg-gray-3",
        },
        {
            icon: true,
            variant: "outlined",
            color: "secondary",
            size: "regular",
            className: "border-none p-3 inner-border inner-border-gray-light hover:inner-border-gray-dark hover:disabled:inner-border-gray disabled:bg-gray-3",
        },
        {
            icon: true,
            variant: "outlined",
            color: "secondary",
            size: "condensed",
            className: "border-none p-1.5 inner-border inner-border-gray-light hover:inner-border-gray-dark hover:disabled:inner-border-gray-light disabled:bg-gray-3",
        },
    ],
    defaultVariants: {
        size: "regular",
        color: "primary",
        icon: false,
    },
});
const LoadingWrapper = ({ color = "primary", variant, size, }) => {
    const fillColor = {
        primary: colors.primary.DEFAULT,
        secondary: colors.gray.DEFAULT,
        success: colors.success.DEFAULT,
        danger: colors.danger.DEFAULT,
    };
    const fill = {
        contained: colors.white.DEFAULT,
        outlined: colors.gray.dark,
        text: fillColor[color],
        link: fillColor[color],
        ghost: fillColor[color],
    }[variant];
    return (_jsx("div", { className: "relative flex h-4 w-12 items-center justify-center", children: _jsx(Icon, { name: "cb-loading-spin", className: cn("absolute ", size === "condensed" && "!w-4"), fill: fill }) }));
};
const customIcon = ({ icon }) => {
    return React.cloneElement(icon, {
        className: cn(icon?.props?.className || "w-4 h-4"),
    });
};
const Button = React.forwardRef(({ variant = "contained", type = "button", size = "regular", loading = false, full = false, asChild = false, icon = false, startIcon, color, endIcon, className, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    const buttonProps = {
        className: cn(buttonVariants({
            variant,
            color,
            size,
            icon: !!icon,
            full,
        }), className),
        type,
        ref,
        ...props,
    };
    const iconProps = {
        icon,
        children,
    };
    const startIconProps = {
        icon: startIcon,
        children,
        position: "start",
    };
    const endIconProps = {
        icon: endIcon,
        children,
        position: "end",
    };
    const renderIconAndLoading = () => {
        return (_jsx(Comp, { ...buttonProps, children: _jsxs("div", { className: "flex h-full flex-row items-center justify-center gap-2.5", children: [!loading && startIcon ? customIcon(startIconProps) : null, loading && !asChild ? _jsx(LoadingWrapper, { color: color, variant: variant, size: size }) : children, !loading && endIcon ? customIcon(endIconProps) : null] }) }));
    };
    const renderOnlyLoading = () => {
        return (_jsx(Comp, { ...buttonProps, children: _jsx(LoadingWrapper, { color: color, variant: variant, size: size }) }));
    };
    const renderOnlyIcon = () => {
        return (_jsx(Comp, { ...buttonProps, children: _jsx("div", { className: "flex h-full flex-row items-center justify-center", children: loading ? _jsx(Icon, { name: "cb-loading-spin" }) : customIcon(iconProps) }) }));
    };
    if (icon && !children)
        return renderOnlyIcon();
    if (!!startIcon || !!endIcon)
        return renderIconAndLoading();
    if (loading && !asChild)
        return renderOnlyLoading();
    return _jsx(Comp, { ...buttonProps, children: children });
});
export default Button;
